import {
  AUTO_PATH_SOURCE,
  AUTO_PATH_LAYER,
  CUSTOM_PATH_SOURCE,
  CUSTOM_PATH_LAYER,
  POINTS_CIRCLE_LAYER
} from './constants';
import { safeSetSourceData } from './utils';
import { debugTime, pathDebug } from '../utils/debug.js';
import { smartRefreshManager } from '../utils/smartRefresh';

// Update auto path data
export const updateAutoPath = ({
  map,
  mapReady,
  sourcesReady,
  autoPath,
  locations,
  shouldShowAutoPath,
  forceRefresh = false  // Smart refresh: only force when explicitly needed
}) => {
  // Performance timing and debug logging
  const endTiming = debugTime('PATH-RENDERER', 'updateAutoPath');
  pathDebug('updateAutoPath called', {
    mapReady,
    sourcesReady,
    shouldShow: shouldShowAutoPath,
    pathLength: Array.isArray(autoPath) ? autoPath.length : 0,
    forceRefresh
  });

  // Smart refresh decision
  const refreshDecision = smartRefreshManager.shouldRefreshPath(
    'auto', 
    autoPath, 
    locations, 
    { shouldShow: shouldShowAutoPath }
  );
  
  pathDebug('Smart refresh decision', refreshDecision);

  // Check if autoPath is empty or invalid
  const hasValidPath = autoPath && (
    (Array.isArray(autoPath) && autoPath.length >= 2) ||
    (autoPath.type === 'FeatureCollection' && autoPath.features && autoPath.features.length > 0)
  );

  if (!shouldShowAutoPath || !hasValidPath) {
    // If auto path should not be shown or path is incomplete, clear the path
    if (mapReady && sourcesReady) {
      pathDebug('Clearing auto path data');
      const emptyGeoJSON = {
        type: 'Feature',
        properties: { pathType: 'auto' },
        geometry: { type: 'LineString', coordinates: [] }
      };
      safeSetSourceData(map, mapReady, sourcesReady, AUTO_PATH_SOURCE, emptyGeoJSON);
    }
    endTiming();
    return;
  }
  
  if (!map || !mapReady || !sourcesReady) {
    pathDebug('Map or sources not ready, skipping update');
    endTiming();
    return;
  }

  // Skip update if data hasn't changed and we have cached data
  if (!forceRefresh && !refreshDecision.needsRefresh && refreshDecision.useCache) {
    pathDebug('Using cached GeoJSON, skipping expensive update');
    const cachedData = refreshDecision.cachedGeoJSON;
    safeSetSourceData(map, mapReady, sourcesReady, AUTO_PATH_SOURCE, cachedData);
    endTiming();
    return;
  }
  
  try {
    let pathData;
    
    // Handle different autoPath formats
    if (autoPath.type === 'FeatureCollection') {
      // Direct GeoJSON from TP routes
      pathDebug('Using direct GeoJSON from TP routes');
      pathData = autoPath;
    } else if (Array.isArray(autoPath)) {
      // Traditional location ID array format
      pathData = smartRefreshManager.pathToGeoJSON(
        autoPath, 
        locations, 
        'auto', 
        refreshDecision.dataHash
      );
    } else {
      pathDebug('Invalid autoPath format', autoPath, 'warn');
      endTiming();
      return;
    }
    
    pathDebug('Generated path GeoJSON', {
      cached: refreshDecision.useCache,
      type: pathData.type,
      featuresCount: pathData.features ? pathData.features.length : 0,
      coordinatesCount: pathData.geometry ? pathData.geometry.coordinates.length : 0
    });
    
    // Validate path data
    const hasValidGeometry = pathData.type === 'FeatureCollection' 
      ? pathData.features && pathData.features.length > 0
      : pathData.geometry && pathData.geometry.coordinates.length >= 2;
      
    if (!hasValidGeometry) {
      pathDebug('Not enough valid coordinates to render path', null, 'warn');
      endTiming();
      return;
    }
    
    // Use intelligent update strategy
    const shouldUseLayerRecreation = forceRefresh || refreshDecision.needsRefresh;
    
    if (shouldUseLayerRecreation) {
      pathDebug('Using layer recreation for reliable update');
      
      // Remember if layer was visible
      let wasVisible = shouldShowAutoPath;
      if (map.getLayer(AUTO_PATH_LAYER)) {
        wasVisible = map.getLayoutProperty(AUTO_PATH_LAYER, 'visibility') === 'visible';
        // Remove existing layer
        map.removeLayer(AUTO_PATH_LAYER);
      }
      
      // Remove and recreate source
      if (map.getSource(AUTO_PATH_SOURCE)) {
        map.removeSource(AUTO_PATH_SOURCE);
      }
      
      // Recreate source
      map.addSource(AUTO_PATH_SOURCE, {
        type: 'geojson',
        data: pathData
      });
      
      // Recreate layer with dynamic color support
      map.addLayer({
        id: AUTO_PATH_LAYER,
        type: 'line',
        source: AUTO_PATH_SOURCE,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': wasVisible ? 'visible' : 'none'
        },
        paint: {
          // Use dynamic color from feature properties, fallback to blue
          'line-color': [
            'case',
            ['has', 'route_color'],
            ['get', 'route_color'],
            '#2196F3' // Default blue color
          ],
          // Responsive auto path line width
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 2,    // City level: thin lines
            10, 3,   // Medium level
            12, 4,   // Default level: normal lines
            15, 5,   // Street level: thick lines
            18, 7    // Building level: thickest lines
          ],
          'line-opacity': 0.8
        }
      }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer to ensure points are on top
      
      pathDebug('Layer completely recreated');
      
      // Force a map repaint
      setTimeout(() => {
        if (map) {
          map.triggerRepaint();
          pathDebug('Triggered map repaint');
        }
      }, 50);
    } else {
      // Fast update method - use when data structure is same
      pathDebug('Using fast source data update');
      safeSetSourceData(map, mapReady, sourcesReady, AUTO_PATH_SOURCE, pathData);
    }
  } catch (err) {
    pathDebug('Error updating auto path line data', err, 'error');
  }
  
  endTiming();
};

// Update custom path data
export const updateCustomPath = ({
  map,
  mapReady,
  sourcesReady,
  customPath,
  locations,
  shouldShowCustomPath,
  forceRefresh = false
}) => {
  const endTiming = debugTime('PATH-RENDERER', 'updateCustomPath');
  pathDebug('updateCustomPath called', {
    shouldShow: shouldShowCustomPath,
    pathLength: Array.isArray(customPath) ? customPath.length : 0,
    forceRefresh
  });

  // Smart refresh decision
  const refreshDecision = smartRefreshManager.shouldRefreshPath(
    'custom', 
    customPath, 
    locations, 
    { shouldShow: shouldShowCustomPath }
  );
  
  pathDebug('Smart refresh decision for custom path', refreshDecision);

  // Check if customPath is empty or invalid (similar to autoPath logic)
  const hasValidCustomPath = customPath && (
    (Array.isArray(customPath) && customPath.length >= 2) ||
    (customPath.type === 'FeatureCollection' && customPath.features && customPath.features.length > 0)
  );

  if (!shouldShowCustomPath || !hasValidCustomPath) {
    // If custom path should not be shown or path is incomplete, clear the path
    if (mapReady && sourcesReady) {
      pathDebug('Clearing custom path data');
      const emptyGeoJSON = {
        type: 'Feature',
        properties: { pathType: 'custom' },
        geometry: { type: 'LineString', coordinates: [] }
      };
      safeSetSourceData(map, mapReady, sourcesReady, CUSTOM_PATH_SOURCE, emptyGeoJSON);
    }
    endTiming();
    return;
  }
  
  if (!map || !mapReady || !sourcesReady) {
    pathDebug('Map or sources not ready, skipping update');
    endTiming();
    return;
  }

  // Skip update if data hasn't changed and we have cached data
  if (!forceRefresh && !refreshDecision.needsRefresh && refreshDecision.useCache) {
    pathDebug('Using cached GeoJSON for custom path, skipping expensive update');
    const cachedData = refreshDecision.cachedGeoJSON;
    safeSetSourceData(map, mapReady, sourcesReady, CUSTOM_PATH_SOURCE, cachedData);
    endTiming();
    return;
  }

  try {
    let pathData;
    
    // Handle different customPath formats (same as autoPath)
    if (customPath.type === 'FeatureCollection') {
      // Direct GeoJSON from Route Editor
      pathDebug('Using direct GeoJSON from Route Editor');
      pathData = customPath;
    } else if (Array.isArray(customPath)) {
      // Traditional location ID array format
      pathData = smartRefreshManager.pathToGeoJSON(
        customPath, 
        locations, 
        'custom', 
        refreshDecision.dataHash
      );
    } else {
      pathDebug('Invalid customPath format', customPath, 'warn');
      endTiming();
      return;
    }
    
    // Validate path data
    const hasValidGeometry = pathData.type === 'FeatureCollection' 
      ? pathData.features && pathData.features.length > 0
      : pathData.geometry && pathData.geometry.coordinates.length >= 2;
      
    if (!hasValidGeometry) {
      pathDebug('Not enough valid coordinates for custom path', null, 'warn');
      endTiming();
      return;
    }
    
    pathDebug('Updating custom path source', {
      cached: refreshDecision.useCache,
      type: pathData.type,
      featuresCount: pathData.features ? pathData.features.length : 0,
      coordinatesCount: pathData.geometry ? pathData.geometry.coordinates.length : 'N/A (FeatureCollection)'
    });
    
    // Use intelligent update strategy
    const shouldUseLayerRecreation = forceRefresh || refreshDecision.needsRefresh || !map.getLayer(CUSTOM_PATH_LAYER);
    
    if (shouldUseLayerRecreation) {
      pathDebug('Using layer recreation for reliable custom path update');
      
      // Remember if layer was visible
      let wasVisible = shouldShowCustomPath;
      if (map.getLayer(CUSTOM_PATH_LAYER)) {
        wasVisible = map.getLayoutProperty(CUSTOM_PATH_LAYER, 'visibility') === 'visible';
        // Remove existing layer
        map.removeLayer(CUSTOM_PATH_LAYER);
      }
      
      // Remove and recreate source
      if (map.getSource(CUSTOM_PATH_SOURCE)) {
        map.removeSource(CUSTOM_PATH_SOURCE);
      }
      
      // Recreate source
      map.addSource(CUSTOM_PATH_SOURCE, {
        type: 'geojson',
        data: pathData
      });
      
      // Recreate layer with dynamic color support (same as autoPath)
      map.addLayer({
        id: CUSTOM_PATH_LAYER,
        type: 'line',
        source: CUSTOM_PATH_SOURCE,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': wasVisible ? 'visible' : 'none'
        },
        paint: {
          // Use dynamic color from feature properties, fallback to orange
          'line-color': [
            'case',
            ['has', 'route_color'],
            ['get', 'route_color'],
            '#FF5722' // Default orange color
          ],
          // Responsive custom path line width
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 1.5,  // City level: thin lines
            10, 2.5, // Medium level
            12, 3,   // Default level: normal lines
            15, 4,   // Street level: thick lines
            18, 6    // Building level: thickest lines
          ],
          'line-opacity': 0.8,
          // Static dash pattern (dasharray cannot be interpolated)
          'line-dasharray': [2, 2]
        }
      }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer to ensure points are on top
      
      pathDebug('Custom path layer completely recreated');
      
      // Force a map repaint
      setTimeout(() => {
        if (map) {
          map.triggerRepaint();
          pathDebug('Triggered map repaint for custom path');
        }
      }, 50);
    } else {
      // Fast update method - use when data structure is same
      pathDebug('Using fast source data update for custom path');
      const success = safeSetSourceData(map, mapReady, sourcesReady, CUSTOM_PATH_SOURCE, pathData);
      
      if (success) {
        pathDebug('Custom path updated successfully');
      }
    }
  } catch (err) {
    pathDebug('Error updating custom path line data', err, 'error');
  }
  
  endTiming();
};

// Set path visibility
export const setPathVisibility = (map, mapReady, shouldShowAutoPath, shouldShowCustomPath) => {
  if (!map || !mapReady) return;
  
  try {
    // Set auto path line visibility
    map.setLayoutProperty(
      AUTO_PATH_LAYER,
      'visibility',
      shouldShowAutoPath ? 'visible' : 'none'
    );
    
    // Set custom path line visibility
    map.setLayoutProperty(
      CUSTOM_PATH_LAYER,
      'visibility',
      shouldShowCustomPath ? 'visible' : 'none'
    );
  } catch (err) {
    console.error("Error toggling layer visibility:", err);
  }
}; 